import { useCallback, useState } from 'react';
import { history, useModel } from 'umi';
import type { LoginInfo } from '@/services/authority/login/login';
import { getUserInfo } from '@/services/authority/login/login';
import type { ThirdPartyLoginType } from '@/typings/user';
import { FeishuLoginStatus } from '@/typings/user';
import {
  login,
  logout,
  transferUserInfo,
  feishuLogin,
  thirdPartyLogin,
} from '@/services/authority/login/login';
import { message } from 'antd';
import * as Sentry from '@sentry/browser';
import { identifyUser, resetPosthogUser } from '@/posthog';

export default function useAuthModel() {
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const { setInitialState } = useModel('@@initialState');

  const toLogin = async (params: LoginInfo) => {
    setLoading(true);
    const [response, e] = await login(params);
    setError(e);
    if (response) {
      const currentUser = transferUserInfo(response);
      // ! 登录接口中信息不全，用户信息以 /auth/current_user 为准
      const userInfo = await getUserInfo();
      const user = { ...currentUser, ...userInfo };
      await setInitialState((s) => ({ ...s, currentUser: user }));
      Sentry.setUser({
        id: currentUser.id,
        username: currentUser.name,
        email: currentUser.email,
      });
      identifyUser(currentUser?.email, {
        name: currentUser?.name,
        email: currentUser.email,
      });
      history.replace(currentUser.home ?? '/');
      message.success('登录成功');
    }
    setLoading(false);
  };

  const toFeishuLogin: (code: string) => Promise<string | undefined> = useCallback(
    async (code: string) => {
      setLoading(true);

      // feishuLogin 配置了 getResponse: true，所以返回的是完整的响应对象
      const [response] = await feishuLogin({ code });

      // 如果返回的数据中有用户原有的中台信息 && 返回的状态是成功
      if (response?.data?.data?.bv_crm && response?.data?.status === FeishuLoginStatus.SUCCESS) {
        const currentUser = transferUserInfo(response.data.data.bv_crm);
        // ! 登录接口中信息不全，用户信息以 /auth/current_user 为准
        const userInfo = await getUserInfo();
        const user = { ...currentUser, ...userInfo };
        await setInitialState((s) => ({ ...s, currentUser: user }));
        Sentry.setUser({
          id: currentUser.id,
          username: currentUser.name,
          email: currentUser.email,
        });
        identifyUser(currentUser?.email, {
          name: currentUser?.name,
          email: currentUser.email,
        });
        history.replace(currentUser.home ?? '/');
        message.success('登录成功');
        setLoading(false);
        return;
      } else {
        // 失败的情况下，返回错误信息
        setLoading(false);
        return response?.data?.msg ?? '登录失败，请联系管理员';
      }
    },
    [setInitialState],
  );

  const toThirdPartyLogin: ({
    code,
    type,
  }: {
    code: string;
    type: ThirdPartyLoginType;
  }) => Promise<string | undefined> = useCallback(
    async ({ code, type }) => {
      setLoading(true);
      const [response, err] = await thirdPartyLogin({ code, type });

      if (response) {
        const currentUser = transferUserInfo(response.bv_crm);
        // ! 登录接口中信息不全，用户信息以 /auth/current_user 为准
        const userInfo = await getUserInfo();
        const user = { ...currentUser, ...userInfo };
        await setInitialState((s) => ({ ...s, currentUser: user }));
        Sentry.setUser({
          id: currentUser.id,
          username: currentUser.name,
          email: currentUser.email,
        });
        identifyUser(currentUser?.email, {
          name: currentUser?.name,
          email: currentUser.email,
        });
        history.replace(currentUser.home ?? '/');
        message.success('登录成功');
      } else {
        // 失败的情况下，返回错误信息
        setLoading(false);
        return err;
      }
    },
    [setInitialState],
  );

  const toLogout = async () => {
    await logout();
    if (window.location.pathname !== LoginPath) {
      // 使用location.replace会刷新页面。不会保留已有的redux的数据。
      // history.replace会造成部分页面退出重新登录后还留有上一次登录的数据
      window.location.replace(LoginPath);
    }
    Sentry.setUser({});
    resetPosthogUser();
  };

  return {
    error,
    loading,
    toLogin,
    toFeishuLogin,
    toThirdPartyLogin,
    toLogout,
  };
}
